import moment from "moment-timezone";

/**
 * Indian Standard Time (IST) timezone
 */
const IST_TIMEZONE = "Asia/Kolkata";

/**
 * Parse a timestamp from database (UTC format) and return a moment object in UTC
 * Handles both ISO strings and MySQL datetime format (YYYY-MM-DD HH:MM:ss)
 * 
 * @param {string|Date|number|null} timestamp - Timestamp from database (UTC)
 * @returns {moment.Moment|null} - Moment object in UTC, or null if invalid
 */
export const parseUTCTimestamp = (timestamp) => {
  if (!timestamp) return null;

  let momentObj = null;

  // Handle Date objects
  if (timestamp instanceof Date) {
    momentObj = moment.utc(timestamp);
  }
  // Handle numbers (Unix timestamps)
  else if (typeof timestamp === "number") {
    // If it's in seconds, convert to milliseconds
    const ms = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    momentObj = moment.utc(ms);
  }
  // Handle strings
  else if (typeof timestamp === "string") {
    // Check if it's MySQL datetime format (YYYY-MM-DD HH:MM:ss)
    const mysqlFormat = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    if (mysqlFormat.test(timestamp.trim())) {
      // Parse as UTC (MySQL datetime format without timezone info is treated as UTC)
      momentObj = moment.utc(timestamp, "YYYY-MM-DD HH:mm:ss");
    } else {
      // Try parsing as ISO string or other formats
      momentObj = moment.utc(timestamp);
    }
  }

  // Validate the moment object
  if (!momentObj || !momentObj.isValid()) {
    return null;
  }

  return momentObj;
};

/**
 * Convert UTC timestamp to IST and format for display
 * Shows relative time (e.g., "5m ago", "2h ago") or absolute date
 * All time calculations are done in IST timezone to ensure accurate relative times
 * 
 * @param {string|Date|number|null} timestamp - Timestamp from database (UTC)
 * @returns {string} - Formatted string for display in IST
 */
export const formatTimestampForDisplay = (timestamp) => {
  const utcMoment = parseUTCTimestamp(timestamp);
  if (!utcMoment) return "";

  // Convert UTC timestamp to IST timezone
  const istMoment = utcMoment.tz(IST_TIMEZONE);
  
  // Get current time in IST timezone (not UTC)
  const now = moment().tz(IST_TIMEZONE);
  // Calculate differences in IST timezone
  // This ensures "X ago" is calculated based on IST, not UTC
  const diffMinutes = Math.floor(now.diff(istMoment, "minutes", true));
  const diffHours = Math.floor(now.diff(istMoment, "hours", true));
  const diffDays = Math.floor(now.diff(istMoment, "days", true));

  // Handle negative differences (future timestamps)
  if (diffMinutes < 0) return "Just now";

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return istMoment.format("MMM DD, YYYY");
};

/**
 * Format timestamp for detailed display (with time)
 * Shows date and time in IST
 * 
 * @param {string|Date|number|null} timestamp - Timestamp from database (UTC)
 * @param {string} format - Moment format string (default: "MMM DD, YYYY hh:mm A")
 * @returns {string} - Formatted string for display in IST
 */
export const formatTimestampDetailed = (timestamp, format = "MMM DD, YYYY hh:mm A") => {
  const utcMoment = parseUTCTimestamp(timestamp);
  if (!utcMoment) return "";

  // Convert to IST and format
  return utcMoment.tz(IST_TIMEZONE).format(format);
};

/**
 * Get timestamp value for sorting (returns UTC timestamp as number)
 * Use this for comparing/sorting timestamps
 * 
 * @param {string|Date|number|null} timestamp - Timestamp from database (UTC)
 * @returns {number} - Timestamp in milliseconds (UTC), or 0 if invalid
 */
export const getTimestampForSorting = (timestamp) => {
  const utcMoment = parseUTCTimestamp(timestamp);
  if (!utcMoment) return 0;
  return utcMoment.valueOf(); // Returns milliseconds since epoch
};

/**
 * Normalize timestamp to ISO string for consistent storage/comparison
 * Converts any valid timestamp format to ISO string (UTC)
 * 
 * @param {string|Date|number|null} timestamp - Timestamp from database (UTC)
 * @returns {string|null} - ISO string in UTC, or null if invalid
 */
export const normalizeTimestampToISO = (timestamp) => {
  const utcMoment = parseUTCTimestamp(timestamp);
  if (!utcMoment) return null;
  return utcMoment.toISOString();
};

/**
 * Format timestamp for input fields (date-time-local format)
 * Converts UTC to IST and formats for HTML datetime-local input
 * 
 * @param {string|Date|number|null} timestamp - Timestamp from database (UTC)
 * @returns {string} - Formatted string for datetime-local input (YYYY-MM-DDTHH:mm)
 */
export const formatTimestampForInput = (timestamp) => {
  const utcMoment = parseUTCTimestamp(timestamp);
  if (!utcMoment) return "";

  // Convert to IST and format for datetime-local input
  return utcMoment.tz(IST_TIMEZONE).format("YYYY-MM-DDTHH:mm");
};

/**
 * Convert IST datetime string back to UTC for database storage
 * Parses datetime-local input (IST) and converts to UTC ISO string
 * 
 * @param {string} istDateTime - DateTime string in IST (YYYY-MM-DDTHH:mm format)
 * @returns {string|null} - ISO string in UTC, or null if invalid
 */
export const convertISTToUTC = (istDateTime) => {
  if (!istDateTime) return null;

  try {
    // Parse as IST
    const istMoment = moment.tz(istDateTime, "YYYY-MM-DDTHH:mm", IST_TIMEZONE);
    if (!istMoment.isValid()) return null;

    // Convert to UTC and return ISO string
    return istMoment.utc().toISOString();
  } catch (error) {
    console.error("Error converting IST to UTC:", error);
    return null;
  }
};

/**
 * Get current timestamp in UTC format for database storage
 * Returns MySQL datetime format string (YYYY-MM-DD HH:MM:SS) in UTC
 * 
 * @returns {string} - Current timestamp in MySQL format (UTC)
 */
export const getCurrentUTCTimestamp = () => {
  return moment.utc().format("YYYY-MM-DD HH:mm:ss");
};

/**
 * Format UTC timestamp for display (shows UTC time, not converted to IST)
 * Shows relative time (e.g., "5m ago", "2h ago") or absolute date in UTC
 * 
 * @param {string|Date|number|null} timestamp - Timestamp from database (UTC)
 * @returns {string} - Formatted string for display in UTC
 */
export const formatTimestampForDisplayUTC = (timestamp) => {
  const utcMoment = parseUTCTimestamp(timestamp);
  if (!utcMoment) return "";

  // Keep in UTC timezone (don't convert to IST)
  const now = moment.utc();
  // Calculate differences in UTC timezone
  const diffMinutes = Math.floor(now.diff(utcMoment, "minutes", true));
  const diffHours = Math.floor(now.diff(utcMoment, "hours", true));
  const diffDays = Math.floor(now.diff(utcMoment, "days", true));

  // Handle negative differences (future timestamps)
  if (diffMinutes < 0) return "Just now";

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  // Format in UTC (add UTC label to make it clear)
  return utcMoment.format("MMM DD, YYYY HH:mm") + " UTC";
};

